/**
 * Отрисовка содержимого карточки по виду инструмента.
 *
 * Общее правило для всех видов: то, чего в данных нет, не рисуется нулём.
 * Разрыв в ряду остаётся разрывом, пустое сравнение — пустым.
 */

import type { EChartsOption } from "echarts";

import type { QueryResultDTO, SeriesPoint } from "../api/types";
import { baseOption, Chart, SECOND_COLOR, SERIES_COLOR } from "./Chart";
import { formatBucket, formatDate, formatNumber } from "./format";
import type { PanelKind } from "./tools";

const SERVICE_KEYS = new Set([
  "metric_label",
  "granularity",
  "points",
  "people",
  "a",
  "b",
  "comparable",
  "program",
  "split_at",
  "peak_date",
]);

export function PanelBody({ kind, result }: { kind: PanelKind; result: QueryResultDTO }) {
  switch (kind) {
    case "series":
      return <SeriesBody result={result} />;
    case "topVideos":
      return <TopVideosBody result={result} />;
    case "people":
      return <PeopleBody result={result} />;
    case "compare":
      return <CompareBody result={result} />;
    case "growth":
      return <GrowthBody result={result} />;
    default:
      return <Tiles result={result} />;
  }
}

// ------------------------------------------------------------------- плитки

function Tiles({ result }: { result: QueryResultDTO }) {
  // null остаётся плиткой с прочерком, а не пропадает: «не посчитано» надо
  // показать. Исчезнувшее поле выглядит как поле, которого и не должно быть.
  const entries = Object.entries(result.numbers).filter(
    ([key, value]) =>
      !SERVICE_KEYS.has(key) &&
      (value === null || (!Array.isArray(value) && typeof value !== "object")),
  );
  if (entries.length === 0) return <p className="muted">Числа для этого набора фильтров не посчитаны.</p>;
  return (
    <div className="tiles">
      {entries.map(([key, value]) => (
        <div className="tile" key={key}>
          <span className="tile__value">{formatNumber(value)}</span>
          <span className="tile__label">{result.labels[key] ?? key}</span>
        </div>
      ))}
    </div>
  );
}

// --------------------------------------------------------------------- ряд

function SeriesBody({ result }: { result: QueryResultDTO }) {
  const points = (result.numbers.points as SeriesPoint[] | undefined) ?? [];
  const granularity = (result.numbers.granularity as string | null) ?? null;
  const gaps = Number(result.numbers.intervals_without_data ?? 0);
  const partial = Number(result.numbers.partial_intervals ?? 0);

  if (points.length === 0) return <p className="muted">Ряд пуст.</p>;

  // Все точки пустые — измерять было нечего: либо выпусков по фильтрам нет,
  // либо у них нет статистики. Пустые оси выглядели бы как «данные есть, но
  // всё по нулю»; причина названа в оговорках ниже.
  if (points.every((point) => point.value === null)) {
    return <p className="denied">График не построен: измерять по этим фильтрам нечего.</p>;
  }

  const option: EChartsOption = {
    ...baseOption(),
    xAxis: {
      type: "category",
      data: points.map((point) => formatBucket(point.date, granularity)),
      axisTick: { show: false },
    },
    yAxis: { type: "value", splitLine: { lineStyle: { type: "dashed" } } },
    series: [
      {
        type: "line",
        smooth: false,
        showSymbol: points.length <= 60,
        // Ключевое: null — интервал без данных, и линия обязана в нём
        // рваться. Соединить края значило бы нарисовать данные, которых нет.
        connectNulls: false,
        // Неполный интервал помечается полым кружком: он ниже соседних не
        // из-за спада, а потому что обрезан краем периода или несобранными
        // днями. Нарисованный наравне с полными, он читается как провал.
        data: points.map((point) =>
          point.partial
            ? {
                value: point.value,
                symbol: "circle",
                symbolSize: 9,
                itemStyle: { color: "transparent", borderColor: SERIES_COLOR, borderWidth: 2 },
              }
            : point.value,
        ),
        lineStyle: { width: 2, color: SERIES_COLOR },
        itemStyle: { color: SERIES_COLOR },
        areaStyle: { opacity: 0.08, color: SERIES_COLOR },
      },
    ],
  };

  return (
    <>
      <Chart option={option} height={280} />
      <div className="series-foot">
        <Tiles result={result} />
        {gaps > 0 && (
          <p className="warn">
            Разрывов на графике: {gaps}. Это интервалы без собранной статистики, а не нули.
          </p>
        )}
        {partial > 0 && (
          <p className="warn">
            Полым кружком отмечены интервалы, покрытые не полностью ({partial}): их обрезал край
            периода или несобранные дни. Они ниже соседних не из-за спада.
          </p>
        )}
      </div>
    </>
  );
}

// -------------------------------------------------------------- топ выпусков

function TopVideosBody({ result }: { result: QueryResultDTO }) {
  const rows = result.videos;
  if (rows.length === 0) return <p className="muted">Выпусков по этим фильтрам нет.</p>;

  const metricKey = Object.keys(rows[0] ?? {}).find(
    (key) => !["youtube_id", "title", "published_at", "url"].includes(key),
  );
  if (!metricKey) return <VideoTable rows={rows} />;

  const top = [...rows].slice(0, 10).reverse();
  const option: EChartsOption = {
    ...baseOption(),
    grid: { left: 8, right: 24, top: 8, bottom: 8, containLabel: true },
    xAxis: { type: "value", splitLine: { lineStyle: { type: "dashed" } } },
    yAxis: {
      type: "category",
      data: top.map((row) => truncate(row.title, 42)),
      axisTick: { show: false },
    },
    series: [
      {
        type: "bar",
        data: top.map((row) => Number(row[metricKey] ?? 0)),
        itemStyle: { color: SERIES_COLOR, borderRadius: [0, 3, 3, 0] },
        barMaxWidth: 18,
      },
    ],
  };

  return (
    <>
      <Chart option={option} height={Math.max(200, top.length * 28)} />
      <VideoTable rows={rows.slice(0, 10)} metricKey={metricKey} label={result.labels[metricKey]} />
    </>
  );
}

// -------------------------------------------------------------------- люди

function PeopleBody({ result }: { result: QueryResultDTO }) {
  const people = (result.numbers.people as { name: string; episodes: number }[] | undefined) ?? [];
  if (people.length === 0) return <p className="muted">Никого не нашлось по этим фильтрам.</p>;

  const rows = [...people].slice(0, 12).reverse();
  const option: EChartsOption = {
    ...baseOption(),
    grid: { left: 8, right: 24, top: 8, bottom: 8, containLabel: true },
    xAxis: { type: "value", splitLine: { lineStyle: { type: "dashed" } } },
    yAxis: { type: "category", data: rows.map((row) => row.name), axisTick: { show: false } },
    series: [
      {
        type: "bar",
        data: rows.map((row) => row.episodes),
        itemStyle: { color: SERIES_COLOR, borderRadius: [0, 3, 3, 0] },
        barMaxWidth: 18,
      },
    ],
  };
  return <Chart option={option} height={Math.max(200, rows.length * 26)} />;
}

// ---------------------------------------------------------------- сравнение

type Side = { name: string; episodes: number; average: number; total: number };

function CompareBody({ result }: { result: QueryResultDTO }) {
  const a = result.numbers.a as Side | undefined;
  const b = result.numbers.b as Side | undefined;
  const comparable = Boolean(result.numbers.comparable);
  const difference = result.numbers.difference_percent;
  if (!a || !b) return <p className="muted">Сравнение не посчитано.</p>;

  const option: EChartsOption = {
    ...baseOption(),
    xAxis: { type: "category", data: [a.name, b.name], axisTick: { show: false } },
    yAxis: { type: "value", splitLine: { lineStyle: { type: "dashed" } } },
    series: [
      {
        type: "bar",
        data: [
          { value: a.average, itemStyle: { color: SERIES_COLOR } },
          { value: b.average, itemStyle: { color: SECOND_COLOR } },
        ],
        barMaxWidth: 64,
        itemStyle: { borderRadius: [3, 3, 0, 0] },
      },
    ],
  };

  return (
    <>
      <Chart option={option} height={220} />
      <div className="tiles">
        {[a, b].map((side) => (
          <div className="tile" key={side.name}>
            <span className="tile__value">{formatNumber(side.average)}</span>
            <span className="tile__label">
              {side.name}: в среднем на выпуск, выпусков {formatNumber(side.episodes)}
            </span>
          </div>
        ))}
      </div>
      {/* Процент не приводится, когда у стороны нет статистики: разница
          с неизвестным — не «минус сто процентов». */}
      {comparable && difference !== null && difference !== undefined ? (
        <p className="muted">Разница: {formatNumber(difference)} %</p>
      ) : (
        <p className="warn">Процент разницы не приводится: у одной из сторон нет статистики.</p>
      )}
    </>
  );
}

// -------------------------------------------------------------------- рост

function GrowthBody({ result }: { result: QueryResultDTO }) {
  const numbers = result.numbers as Record<string, number | string | null>;
  const option: EChartsOption = {
    ...baseOption(),
    legend: { top: 0, data: ["Программа", "Канал"] },
    xAxis: {
      type: "category",
      data: ["Первая половина", "Вторая половина"],
      axisTick: { show: false },
    },
    yAxis: { type: "value", splitLine: { lineStyle: { type: "dashed" } } },
    series: [
      {
        name: "Программа",
        type: "bar",
        data: [Number(numbers.program_first_half ?? 0), Number(numbers.program_second_half ?? 0)],
        itemStyle: { color: SERIES_COLOR, borderRadius: [3, 3, 0, 0] },
        barMaxWidth: 48,
      },
      {
        name: "Канал",
        type: "bar",
        data: [Number(numbers.channel_first_half ?? 0), Number(numbers.channel_second_half ?? 0)],
        itemStyle: { color: SECOND_COLOR, borderRadius: [3, 3, 0, 0] },
        barMaxWidth: 48,
      },
    ],
  };
  return (
    <>
      <Chart option={option} height={240} />
      <Tiles result={result} />
    </>
  );
}

// ------------------------------------------------------------------ таблица

function VideoTable({
  rows,
  metricKey,
  label,
}: {
  rows: QueryResultDTO["videos"];
  metricKey?: string;
  label?: string;
}) {
  return (
    <div className="table-scroll">
      <table className="table">
        <thead>
          <tr>
            <th>Дата</th>
            <th>Выпуск</th>
            {metricKey && <th className="num">{label ?? metricKey}</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.youtube_id}>
              <td className="nowrap">{formatDate(row.published_at)}</td>
              <td>
                <a href={row.url} target="_blank" rel="noreferrer">
                  {row.title}
                </a>
              </td>
              {metricKey && <td className="num">{formatNumber(row[metricKey])}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const truncate = (text: string, limit: number) =>
  text.length > limit ? `${text.slice(0, limit - 1)}…` : text;
